import {
  Box,
  Container,
  Flex,
  Heading,
  List,
  ListItem,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import { useModalAction } from '@components/common/modal/modal.context'
import ChakraNextImage from '@components/ui/chakra-next-image'
import { useStore } from '@contexts/store'
import { useGetShopPaymentMethods } from '@framework/get-shop-partners'
import { getSocialIcon } from '@helper/globalHelpers'
import { useAnalyticsEvents } from '@hooks/use-analytics-events'
import { ROUTES } from '@utils/routes'
import { DisplayUploadCareAsset } from 'hooks/useUploadCare'
import Link from 'next/link'
import { useRouter } from 'next/router'

const siteSettings = {
  author: {
    name: 'Powered by QShop',
    email: 'hello@qshop.ng',
    phone: '  ',
    address: '  ',
    websiteUrl: 'https://qshop.tech',
  },
}

export const Footer = () => {
  const router = useRouter()
  const { storeData: shop } = useStore()
  const { methods: paymentMethods } = useGetShopPaymentMethods(shop?._id)
  const { openModal } = useModalAction()

  const { trackContactUsClick } = useAnalyticsEvents()

  if (!shop) return null
  const {
    logo_uuid,
    name: siteName,
    bio,
    socials: socialsList,
    config,
  } = shop ?? {}

  const { storefront } = config ?? {}
  const showNameWithLogo = (storefront?.showNameWithLogo ?? true) || !logo_uuid

  const showStoreDetailsLink = storefront?.showStoreDetails
  const showSocialLinks =
    storefront?.showSocialLinks && socialsList && socialsList?.length >= 1
  const socials = socialsList?.filter(
    (x: any) => !x?.category || x?.category === 'social',
  )

  return (
    <Box mt={['4rem', '5rem']}>
      <Container
        maxW="container.xxl"
        mx="auto"
        px={['1rem', '1.5rem', '2rem', '2.5rem']}
      >
        <Flex
          gap={{
            base: '1.25rem',
            sm: '2.25rem',
            md: '2.50rem',
            lg: '2.75rem',
          }}
          direction={{ base: 'column', md: 'row' }}
          // justifyContent="space-between"
          py="0.8rem"
        >
          <Box minW={{ md: '33%' }} maxW={{ md: '33%' }}>
            <Flex
              direction="column"
              textAlign={{ base: 'center', md: 'start' }}
              justifyContent={{ base: 'center', md: 'start' }}
              alignItems={{ base: 'center', md: 'start' }}
              mx={{ sm: 'auto' }}
              ml={{ sm: 0 }}
              gap={['0.125rem', '0.5rem']}
              className="flex flex-col text-center sm:ltr:text-left sm:rtl:text-right pb-6 sm:pb-5"
              cursor="pointer"
              onClick={() => router.push(ROUTES.HOME)}
            >
              {logo_uuid && (
                <DisplayUploadCareAsset
                  src={logo_uuid}
                  dimensions={{
                    width: showNameWithLogo ? 80 : 160,
                    height: 80,
                  }}
                  alt={siteName ?? 'logo'}
                  options={{ resize: 'x120' }}
                />
              )}
              {showNameWithLogo ? (
                <Heading
                  my={['.2em', 'em']}
                  fontSize={['1em', '1.1em']}
                  margin={['0', '0 auto', '0', '0']}
                >
                  {shop?.name}
                </Heading>
              ) : null}
              <Text
                textAlign={{ md: 'left' }}
                className="text-brand-muted text-sm leading-7 lg:leading-[27px] lg:text-15px"
              >
                {bio}
              </Text>
            </Flex>
          </Box>
          <Flex flexGrow="1" justifyContent={{ md: 'space-between' }}>
            {showStoreDetailsLink || showSocialLinks ? (
              <Flex
                flexDirection="column"
                alignItems={{ base: 'center', md: 'start' }}
                position="relative"
                left={{ md: '9%', lg: '12%', xl: '15%' }}
                minW={{ base: '50%', lg: '40%' }}
              >
                {showStoreDetailsLink && (
                  <Flex
                    align={{ base: 'center', md: 'start' }}
                    textAlign={{ base: 'center', md: 'start' }}
                    direction="column"
                    mb="3"
                  >
                    <Text
                      as="h3"
                      fontWeight={'medium'}
                      className="text-brand-dark text-base lg:text-[17px] lg:leading-7 font-medium mb-4 sm:mb-5 lg:mb-6 pb-0.5"
                    >
                      About Us
                    </Text>
                    <List
                      as="ul"
                      alignItems={{ base: 'center', md: 'start' }}
                      className="flex flex-col space-y-3 text-sm lg:text-15px"
                    >
                      <ListItem className="flex items-baseline">
                        <Link
                          href={ROUTES.ABOUT_US}
                          className="transition-colors duration-200 hover:text-brand-dark"
                          passHref
                          prefetch={false}
                        >
                          About us
                        </Link>
                      </ListItem>
                      <ListItem className="flex items-baseline">
                        <Link
                          className="transition-colors duration-200 hover:text-brand-dark"
                          href={ROUTES.CONTACT_US}
                          onClick={trackContactUsClick}
                          prefetch={false}
                        >
                          Contact us
                        </Link>
                      </ListItem>
                    </List>
                  </Flex>
                )}
                {showSocialLinks && (
                  <List
                    as="ul"
                    ms={0}
                    rowGap="4"
                    textAlign={{ base: 'center', md: 'start' }}
                    maxW={{ base: '150px', xl: 'initial' }}
                    display="flex"
                    flexWrap="wrap"
                    position="relative"
                    left={{ md: -3.5 }}
                    justifyContent={{ base: 'center', md: 'start' }}
                  >
                    {socials.map(
                      (
                        social: {
                          name: string
                          url: string
                          slug: string
                          [x: string | number | symbol]: unknown
                        },
                        i: number,
                      ) => {
                        let url = social?.url
                        if (!url) return null
                        if (!url.includes('http')) {
                          url = `https://${url}`
                        }
                        return (
                          <ListItem
                            as="li"
                            key={social.slug ?? i}
                            mr={3.5}
                            ml={3.5}
                            className="transition hover:opacity-80 md:mx-0"
                          >
                            <Link
                              href={url ?? '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <ChakraNextImage
                                src={getSocialIcon(
                                  social.slug ??
                                    social?.name?.split(' ')[0]?.toLowerCase(),
                                )}
                                alt={social.name ?? social?.slug ?? 'social'}
                                width={20}
                                height={20}
                                w="20px"
                                h="20px"
                              />
                            </Link>
                          </ListItem>
                        ) as JSX.Element
                      },
                    )}
                  </List>
                )}
              </Flex>
            ) : null}
            <Flex justifyContent={{ base: 'center', md: 'end' }} flexGrow="1">
              <VStack
                spacing="3"
                textAlign={{ base: 'center', md: 'start' }}
                align={{ base: 'center', md: 'start' }}
              >
                <Text
                  as="h3"
                  className="text-brand-dark text-base lg:text-[17px] lg:leading-7 font-medium pb-0.5"
                >
                  Resources
                </Text>

                <Text as={Link} href={ROUTES.STORE_POLICIES} prefetch={false}>
                  <Text as="span" fontSize="15px">
                    Store Policies
                  </Text>
                </Text>

                <Text
                  as="span"
                  cursor={'pointer'}
                  onClick={() => openModal('FIND_MY_ORDER')}
                  fontSize="15px"
                >
                  Find My Order
                </Text>
                {storefront?.showFooterMenu && (
                  <Link href={ROUTES.CONTACT_US} prefetch={false}>
                    <Text as="span" fontSize="15px">
                      Contact
                    </Text>
                  </Link>
                )}
              </VStack>
            </Flex>
          </Flex>

          {/* {storefront?.showEmailSignup && (
            <Flex
              width={{ base: 'full', md: '70%', lg: '30%', xl: '24%' }}
              mx={{ base: 'auto' }}
              direction="column"
              textAlign={{ base: 'center', lg: 'start' }}
            >
              <Heading fontSize={['1em', '1.1em']}>Subscribe Now</Heading>

              <Text fontSize="15px" my=".8em !important">
                Subscribe your email for newsletter and featured news based on
                your interest
              </Text>

              <form>
                <InputGroup _focus={{ ring: '0' }} ring="0">
                  <InputLeftElement
                    pointerEvents="none"
                    color="gray.300"
                    // eslint-disable-next-line react/no-children-prop
                    children={<FiMail />}
                  />
                  <Input
                    type="email"
                    placeholder="Enter your Email Address"
                    _focus={{ ring: '0', borderColor: 'primary.500' }}
                  />
                  <InputRightElement
                    pointerEvents="none"
                    // eslint-disable-next-line react/no-children-prop
                    children={<IoMdSend color={'primary.500'} />}
                  />
                </InputGroup>
              </form>
            </Flex>
          )} */}
        </Flex>

        <Box className="pb-20 lg:pb-7" flexGrow="1">
          <Box className="mx-auto max-w-[1920px]">
            <Box className="flex flex-col md:flex-row text-center md:justify-between border-t border-skin-three pt-6 lg:pt-7">
              <Box className="text-skin-base text-sm leading-7 lg:leading-[27px] lg:text-15px">
                <Link
                  target={!storefront?.hideLogo ? '_blank' : '_top'}
                  rel="noopener noreferrer"
                  href={
                    !storefront?.hideLogo ? siteSettings.author.websiteUrl : '/'
                  }
                  className="text-skin-base transition-colors duration-200 ease-in-out hover:text-skin-primary"
                >
                  {!storefront?.hideLogo ? (
                    siteSettings.author.name
                  ) : (
                    <>
                      &copy; Copyright {new Date().getFullYear()}, {shop?.name}{' '}
                      &reg; All rights reserved
                    </>
                  )}
                </Link>
              </Box>
              {paymentMethods?.length > 0 && (
                <Flex
                  gap={3}
                  className="flex flex-wrap justify-center items-center space-s-4 sm:space-s-5 lg:space-s-7 -mb-1.5 md:mb-0 mx-auto md:mx-0 pt-3.5 md:pt-0"
                >
                  {paymentMethods?.map((integration: any) => (
                    <Tooltip key={integration._id} label={integration.name}>
                      <Box
                        as="li"
                        className="mb-2 md:mb-0 transition hover:opacity-80 inline-flex"
                        key={`payment-list--key${integration?._id}`}
                        // maxW={40}
                        // maxH={40}
                      >
                        <DisplayUploadCareAsset
                          src={integration?.miniImageId ?? integration?.imageId}
                          dimensions={{ width: 40, height: 40 }}
                          options={{ resize: '100x' }}
                          alt={integration.name}
                          // resize="80x40"
                        />
                      </Box>
                    </Tooltip>
                  ))}
                </Flex>
              )}
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  )
}
